import { useEffect, useMemo, useState } from 'react';
import { useMegaMenuState } from '@zillow/zhl-page-frame-module';
import * as ApplicationConfigurationService from 'src/services/config/ApplicationConfigurationService.client';
import { TOP_NAV_DATA } from '../data/production';
import { ZillowNavbarState } from '../data/types';
import { HeaderMegaMenuData } from '../page-frame-types/HeaderMegaMenuData';
export const mergeMicrofrontendModuleData = (
    cachedData: Omit<ZillowNavbarState, 'zhlHost'>,
    microfrontendModuleData: ReturnType<typeof useMegaMenuState>
): Omit<ZillowNavbarState, 'zhlHost'> => {
    const { links } = cachedData;
    const { main } = links;
    const { sections } = main as HeaderMegaMenuData;
    const indexOfHomeLoans = sections.findIndex(section => section.link.text === 'Get a mortgage');
    (cachedData.links.main as HeaderMegaMenuData).sections[indexOfHomeLoans].subsections =
        microfrontendModuleData.subsections;
    return cachedData;
};

export const useZillowNavbarState = (): ZillowNavbarState => {
    const config = ApplicationConfigurationService.ConfigProvider.getInstance();
    const data = useMegaMenuState({ env: config.runtimeConfig.deploymentEnvironment });
    const memoizedProductionData = useMemo(() => mergeMicrofrontendModuleData(TOP_NAV_DATA, data), [data]);

    /**
     * Default to production values for best performance in production, and we can just
     * incur the bigger payload in lower environments.
     */
    const [zillowNavbarData, setZillowNavbarData] = useState<ZillowNavbarState>({
        ...memoizedProductionData,
        zhlHost: config.routeConfiguration.zillowHomeLoansHomePage,
    });

    useEffect(() => {
        const asynchronouslyLoadTopNavData = async () => {
            if (!config.runtimeConfig.isLowerEnvironment) {
                return;
            }
            const { STAGING_TOP_NAV_DATA } = await import(
                /* webpackChunkName: "stagingZillowNavigationData" */
                '../data/staging'
            );
            setZillowNavbarData({
                ...mergeMicrofrontendModuleData(STAGING_TOP_NAV_DATA, data),
                zhlHost: config.routeConfiguration.zillowHomeLoansHomePage,
            });
        };
        asynchronouslyLoadTopNavData();
    }, [config, data]);

    return zillowNavbarData;
};
